import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { Submission } from "@/types";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { ResultsCard } from "@/components/quiz/ResultsCard";

interface AnswerRow {
  id: string;
  questionId: string;
  questionTesto: string;
  questionTipo: "chiusa" | "aperta" | "scala";
  sezione: string | null;
  opzioni: string[] | null;
  spiegazione: string | null;
  rispostaIndice: number | null;
  rispostaTesto: string | null;
  rispostaScala: number | null;
  isCorrect: boolean | null;
  aiSintesi: string | null;
  aiProcessedAt: string | null;
}

export default function Results() {
  const { submissionId = "" } = useParams<{ submissionId: string }>();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ submission: Submission; answers: AnswerRow[] }>(
      `/submissions/${submissionId}/results`,
    )
      .then((r) => {
        setSubmission(r.submission);
        setAnswers(r.answers);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [submissionId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  }

  if (!submission) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md text-center space-y-3">
          <div className="text-5xl">✅</div>
          <p className="text-primary">Test completato.</p>
          <p className="text-secondary text-sm">
            Il dettaglio dei risultati sarà visibile nella tua area una volta
            che il docente avrà rivisto le risposte aperte.
          </p>
          <Link to="/area" className="text-accent underline font-mono text-sm">
            Torna alla mia area →
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <header className="flex justify-between items-center">
        <h1 className="text-2xl font-serif text-primary">I tuoi risultati</h1>
        <Link to="/area" className="font-mono text-xs text-secondary hover:text-primary">
          ← area
        </Link>
      </header>

      <ResultsCard submission={submission} />

      {answers.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">
            Dettaglio risposte ({answers.length})
          </h2>
          {answers.map((a, i) => (
            <AnswerDetail key={a.id} answer={a} index={i + 1} />
          ))}
        </section>
      )}
    </div>
  );
}

function AnswerDetail({ answer: a, index }: { answer: AnswerRow; index: number }) {
  return (
    <Card className="space-y-3">
      <div className="flex items-start gap-3">
        <span className="font-mono text-xs text-muted mt-0.5 shrink-0">{index}.</span>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap gap-1.5 items-center">
            {a.sezione && <Badge variant="outline" color="#5B3E8A">{a.sezione}</Badge>}
            <Badge variant="outline" color="#555577">{tipoLabel(a.questionTipo)}</Badge>
          </div>
          <p className="text-primary font-serif leading-snug">{a.questionTesto}</p>
        </div>
      </div>

      {a.questionTipo === "chiusa" && (
        <ChiusaDetail answer={a} />
      )}

      {a.questionTipo === "aperta" && (
        <ApertaDetail answer={a} />
      )}

      {a.questionTipo === "scala" && a.rispostaScala !== null && (
        <div className="pl-6">
          <span className="text-secondary text-sm">Valore indicato: </span>
          <span className="text-primary font-mono">{a.rispostaScala}</span>
        </div>
      )}
    </Card>
  );
}

function ChiusaDetail({ answer: a }: { answer: AnswerRow }) {
  const opzioneScelta =
    a.opzioni && a.rispostaIndice !== null
      ? a.opzioni[a.rispostaIndice] ?? null
      : null;

  return (
    <div className="pl-6 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {a.isCorrect === true && (
          <span className="font-mono text-xs text-green">✓ Corretta</span>
        )}
        {a.isCorrect === false && (
          <span className="font-mono text-xs text-red-400">✗ Errata</span>
        )}
        {opzioneScelta && (
          <span className="text-secondary text-sm">
            Hai risposto: <span className="text-primary italic">"{opzioneScelta}"</span>
          </span>
        )}
      </div>
      {a.spiegazione && (
        <div className="bg-bg border-l-4 border-border rounded-r-xl p-3">
          <div className="font-mono uppercase text-xs tracking-wider text-muted mb-1">Spiegazione</div>
          <p className="text-secondary text-sm">{a.spiegazione}</p>
        </div>
      )}
    </div>
  );
}

function ApertaDetail({ answer: a }: { answer: AnswerRow }) {
  return (
    <div className="pl-6 space-y-2">
      {a.rispostaTesto ? (
        <blockquote className="border-l-4 border-border pl-3 text-secondary italic text-sm">
          {a.rispostaTesto}
        </blockquote>
      ) : (
        <p className="text-muted text-sm italic">Nessuna risposta fornita.</p>
      )}
      {a.aiSintesi ? (
        <div className="bg-bg border-l-4 border-purple rounded-r-xl p-3">
          <div className="font-mono uppercase text-xs tracking-wider text-secondary mb-1">
            Feedback
          </div>
          <p className="text-primary">{a.aiSintesi}</p>
        </div>
      ) : a.rispostaTesto ? (
        <div className="text-xs text-muted font-mono">Analisi in corso…</div>
      ) : null}
    </div>
  );
}

function tipoLabel(tipo: string) {
  if (tipo === "chiusa") return "scelta multipla";
  if (tipo === "aperta") return "risposta aperta";
  if (tipo === "scala") return "scala";
  return tipo;
}
